import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Phone, Globe, ArrowRight } from 'lucide-react';

const BasicDetailsForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        country: 'India' // Default
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const countries = [
        "India", "Nepal", "Sri Lanka", "Bangladesh", "Nigeria", "Ghana", "Kenya", "Other"
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error("Not authenticated");

            const response = await fetch('/api/student/profile', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error('Failed to update profile');
            }

            // Success - Redirect to Dashboard
            navigate('/student/dashboard');
        } catch (err: any) {
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-lg">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-serif text-[#011E26]">Complete Your Profile</h2>
                    <p className="text-slate-500 text-sm mt-2">Just a few more details to get started</p>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-6 text-center">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Full Name</label>
                        <div className="relative">
                            <User className="absolute left-3 top-3 text-gray-400" size={18} />
                            <input
                                type="text"
                                value={formData.fullName}
                                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all"
                                placeholder="Enter your full name"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Telephone Number</label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-3 text-gray-400" size={18} />
                            <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all"
                                placeholder="+91 98765 43210"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2">Country of Origin</label>
                        <div className="relative">
                            <Globe className="absolute left-3 top-3 text-gray-400" size={18} />
                            <select
                                value={formData.country}
                                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                className="w-full pl-10 p-3 bg-gray-50 border border-gray-200 focus:border-[#009B8F] outline-none rounded-lg transition-all appearance-none"
                            >
                                {countries.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-[#009B8F] hover:bg-[#00857a] text-white text-sm font-bold tracking-widest uppercase transition-all rounded-lg flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                        {loading ? 'Saving...' : (
                            <>Continue <ArrowRight size={16} /></>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default BasicDetailsForm;
